import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Replayer } from 'rrweb';
import 'rrweb/dist/replay/rrweb-replay.min.css';
import type { eventWithTime } from '@rrweb/types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface SessionPlayerModalProps {
  applicationId: string;
  onClose: () => void;
}

interface RecordingMeta {
  _id: string;
  applicationId: string;
  userEmail: string;
  status: string;
  totalEvents: number;
  startedAt: string;
  endedAt: string | null;
  pagesVisited: string[];
  lastPageVisited: string;
  sessionCount?: number;
}

const SessionPlayerModal: React.FC<SessionPlayerModalProps> = ({ applicationId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<eventWithTime[]>([]);
  const [meta, setMeta] = useState<RecordingMeta | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const playerContainerRef = useRef<HTMLDivElement>(null);
  const replayerRef = useRef<Replayer | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);

  // ESC key closes modal (only when not in fullscreen — browser handles ESC for fullscreen exit)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fullscreen toggle
  const handleFullscreen = useCallback(() => {
    if (!modalRef.current) return;
    if (!document.fullscreenElement) {
      modalRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  // Fetch recording data
  useEffect(() => {
    const fetchRecording = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/admin/session-recording/${encodeURIComponent(applicationId)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success && data.events && data.events.length > 0) {
          setEvents(data.events);
          setMeta(data.recording);
          const first = data.events[0].timestamp;
          const last = data.events[data.events.length - 1].timestamp;
          startTimeRef.current = first;
          setTotalTime(last - first);
        } else {
          setError(data.message || 'No recording data found');
        }
      } catch (err) {
        console.error('[SessionPlayer] Fetch error:', err);
        setError('Failed to load session recording');
      } finally {
        setLoading(false);
      }
    };
    fetchRecording();
  }, [applicationId]);

  // Scale the replayer wrapper to fit inside the container
  const scaleReplayerToFit = useCallback(() => {
    if (!playerContainerRef.current) return;
    const wrapper = playerContainerRef.current.querySelector('.replayer-wrapper') as HTMLElement;
    if (!wrapper) return;

    const container = playerContainerRef.current;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;

    // rrweb sets the wrapper size to the recorded page dimensions
    const iframe = wrapper.querySelector('iframe');
    const origW = iframe?.offsetWidth || wrapper.scrollWidth || 1920;
    const origH = iframe?.offsetHeight || wrapper.scrollHeight || 1080;

    const scaleX = containerW / origW;
    const scaleY = containerH / origH;
    const scale = Math.min(scaleX, scaleY, 1); // never upscale

    wrapper.style.transformOrigin = 'top left';
    wrapper.style.transform = `scale(${scale})`;
    wrapper.style.width = `${origW}px`;
    wrapper.style.height = `${origH}px`;

    // Center horizontally if there's leftover space
    const scaledW = origW * scale;
    const offsetX = Math.max(0, (containerW - scaledW) / 2);
    wrapper.style.marginLeft = `${offsetX}px`;
  }, []);

  // Listen for fullscreen change to update state and re-scale
  useEffect(() => {
    const onFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      setTimeout(() => scaleReplayerToFit(), 100);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [scaleReplayerToFit]);

  // Initialize replayer when events are loaded
  useEffect(() => {
    if (events.length === 0 || !playerContainerRef.current) return;

    // Clear previous player
    if (replayerRef.current) {
      try { replayerRef.current.destroy(); } catch (e) { /* ignore */ }
    }
    playerContainerRef.current.innerHTML = '';

    try {
      const replayer = new Replayer(events, {
        root: playerContainerRef.current,
        skipInactive: true,
        showWarning: false,
        showDebug: false,
        speed: speed,
        mouseTail: {
          strokeStyle: '#4A90E2',
          lineWidth: 3,
          duration: 800,
        },
        UNSAFE_replayCanvas: true, // replay canvas elements
        useVirtualDom: false,
        liveMode: false,
        insertStyleRules: [
          `.replayer-mouse { width: 24px; height: 24px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M4 1L4 18L8.5 13.5L13 21L15.5 19.5L11 12L17 12Z' fill='%23000' stroke='%23fff' stroke-width='1.5'/%3E%3C/svg%3E"); background-size: contain; background-repeat: no-repeat; border: none !important; border-radius: 0 !important; }`,
          `.replayer-mouse::after { content: ''; display: none; }`,
          `.replayer-mouse-tail { stroke-linecap: round; }`,
          `[style*="position: fixed"] { position: absolute !important; }`,
          `.MuiModal-root, .MuiDialog-root, .MuiPopover-root, .MuiMenu-root { position: absolute !important; }`,
          `.MuiBackdrop-root { position: absolute !important; }`,
        ],
      });

      replayerRef.current = replayer;

      // Style the replay iframe
      const iframe = playerContainerRef.current.querySelector('iframe');
      if (iframe) {
        iframe.style.border = 'none';
      }

      // Scale to fit container after a brief delay, then auto-play
      requestAnimationFrame(() => {
        setTimeout(() => {
          scaleReplayerToFit();
          // Auto-play once ready
          if (replayerRef.current) {
            replayerRef.current.play(0);
            setIsPlaying(true);
            startProgressTracking();
          }
        }, 100);
      });

      // Re-scale on window resize
      const handleResize = () => scaleReplayerToFit();
      window.addEventListener('resize', handleResize);

      // Listen for finish
      replayer.on('finish', () => {
        setIsPlaying(false);
        setProgress(100);
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
      });

      // Cleanup resize listener
      const cleanupResize = () => window.removeEventListener('resize', handleResize);
      (playerContainerRef.current as any).__cleanupResize = cleanupResize;

    } catch (err) {
      console.error('[SessionPlayer] Replayer init error:', err);
      setError('Failed to initialize session player');
    }

    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (replayerRef.current) {
        try { replayerRef.current.destroy(); } catch (e) { /* ignore */ }
      }
      if (playerContainerRef.current && (playerContainerRef.current as any).__cleanupResize) {
        (playerContainerRef.current as any).__cleanupResize();
      }
    };
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update speed on the replayer
  useEffect(() => {
    if (replayerRef.current) {
      replayerRef.current.setConfig({ speed });
    }
  }, [speed]);

  // Progress tracking
  const startProgressTracking = useCallback(() => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      if (replayerRef.current && totalTime > 0) {
        const ct = replayerRef.current.getCurrentTime();
        setCurrentTime(ct);
        setProgress(Math.min((ct / totalTime) * 100, 100));
      }
    }, 200);
  }, [totalTime]);

  const handlePlay = () => {
    if (!replayerRef.current) return;
    if (progress >= 99.5) {
      // Restart from beginning
      replayerRef.current.play(0);
      setProgress(0);
      setCurrentTime(0);
    } else {
      replayerRef.current.play(currentTime);
    }
    setIsPlaying(true);
    startProgressTracking();
  };

  const handlePause = () => {
    if (!replayerRef.current) return;
    replayerRef.current.pause();
    setIsPlaying(false);
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!replayerRef.current || totalTime === 0) return;
    const pct = parseFloat(e.target.value);
    const seekTime = (pct / 100) * totalTime;
    setProgress(pct);
    setCurrentTime(seekTime);

    if (isPlaying) {
      replayerRef.current.play(seekTime);
    } else {
      replayerRef.current.pause(seekTime);
    }
  };

  const formatTime = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // MP4 Export using canvas capture
  const handleExportMP4 = async () => {
    if (!replayerRef.current || events.length === 0 || !playerContainerRef.current) return;
    setIsExporting(true);
    setExportProgress(0);

    try {
      // Pause current playback
      if (isPlaying) handlePause();

      // Find the replay wrapper element
      const wrapper = playerContainerRef.current.querySelector('.replayer-wrapper') as HTMLElement;
      if (!wrapper) throw new Error('Replay wrapper not found');

      const canvas = document.createElement('canvas');
      const W = 1280;
      const H = 720;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d')!;

      // Set up MediaRecorder
      const stream = canvas.captureStream(10);
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm',
        videoBitsPerSecond: 2500000,
      });
      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      const downloadPromise = new Promise<void>((resolve) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/mp4' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `session-${applicationId}.mp4`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          resolve();
        };
      });

      mediaRecorder.start(1000);

      // Step through replay and capture frames
      const FPS = 5;
      const stepMs = 1000 / FPS;
      const totalSteps = Math.ceil(totalTime / (stepMs * speed));
      const html2canvas = (await import('html2canvas')).default;

      for (let i = 0; i <= totalSteps; i++) {
        const t = Math.min(i * stepMs, totalTime);
        replayerRef.current.pause(t);

        // Wait a bit for the replayer to render the frame
        await new Promise(r => setTimeout(r, 80));

        try {
          const capturedCanvas = await html2canvas(wrapper, {
            width: wrapper.offsetWidth,
            height: wrapper.offsetHeight,
            scale: 1,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
          });

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, W, H);
          // Scale and center
          const scaleX = W / capturedCanvas.width;
          const scaleY = H / capturedCanvas.height;
          const scale = Math.min(scaleX, scaleY);
          const dx = (W - capturedCanvas.width * scale) / 2;
          const dy = (H - capturedCanvas.height * scale) / 2;
          ctx.drawImage(capturedCanvas, dx, dy, capturedCanvas.width * scale, capturedCanvas.height * scale);
        } catch (captureErr) {
          // Draw a placeholder frame if capture fails
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(0, 0, W, H);
          ctx.fillStyle = '#999';
          ctx.font = '16px sans-serif';
          ctx.fillText('Frame capture unavailable', W / 2 - 100, H / 2);
        }

        setExportProgress(Math.round((i / totalSteps) * 100));
      }

      mediaRecorder.stop();
      await downloadPromise;
    } catch (err) {
      console.error('[SessionPlayer] Export error:', err);
      alert('Failed to export video. The session can still be played back in this modal.');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 99999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
  };

  const modalStyle: React.CSSProperties = {
    background: '#1a1a2e', borderRadius: isFullscreen ? '0' : '16px',
    width: isFullscreen ? '100vw' : '95vw', maxWidth: isFullscreen ? '100vw' : '1100px',
    maxHeight: isFullscreen ? '100vh' : '92vh', height: isFullscreen ? '100vh' : 'auto',
    overflow: 'hidden', boxShadow: isFullscreen ? 'none' : '0 25px 60px rgba(0,0,0,0.5)',
    display: 'flex', flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)',
  };

  const controlsStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.3)',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div ref={modalRef} style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h3 style={{ margin: 0, color: '#fff', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A90E2" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              Session Recording
            </h3>
            <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '0.75rem', color: '#94a3b8' }}>
              <span>{applicationId}</span>
              {meta && (
                <>
                  <span>{meta.userEmail}</span>
                  <span style={{ color: meta.status === 'completed' ? '#22c55e' : meta.status === 'recording' ? '#f59e0b' : '#ef4444' }}>
                    {meta.status}
                  </span>
                  <span>{meta.totalEvents} events</span>
                  {meta.sessionCount && meta.sessionCount > 1 && (
                    <span style={{ color: '#818cf8' }}>{meta.sessionCount} sessions</span>
                  )}
                  {meta.pagesVisited?.length > 0 && <span>Pages: {meta.pagesVisited.join(', ')}</span>}
                </>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Fullscreen Button */}
            <button
              onClick={handleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '34px', height: '34px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                background: isFullscreen ? 'rgba(74,144,226,0.3)' : 'rgba(74,144,226,0.15)',
                color: '#93c5fd', cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(74,144,226,0.3)'}
              onMouseLeave={e => e.currentTarget.style.background = isFullscreen ? 'rgba(74,144,226,0.3)' : 'rgba(74,144,226,0.15)'}
            >
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 8 14 8 18"/><polyline points="20 10 16 10 16 6"/><polyline points="14 4 14 8 18 8"/><polyline points="10 20 10 16 6 16"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><polyline points="21 3 14 10"/><polyline points="3 21 10 14"/></svg>
              )}
            </button>
            {/* Download MP4 Button */}
            <button
              onClick={handleExportMP4}
              disabled={isExporting || loading || !!error}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                background: isExporting ? 'rgba(74,144,226,0.3)' : 'rgba(74,144,226,0.15)',
                color: '#93c5fd', cursor: isExporting ? 'not-allowed' : 'pointer',
                fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!isExporting) e.currentTarget.style.background = 'rgba(74,144,226,0.3)'; }}
              onMouseLeave={e => { if (!isExporting) e.currentTarget.style.background = 'rgba(74,144,226,0.15)'; }}
            >
              {isExporting ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  Exporting {exportProgress}%
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Download MP4
                </>
              )}
            </button>
            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                width: '32px', height: '32px', border: 'none', borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,100,100,0.4)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >&times;</button>
          </div>
        </div>

        {/* Player Area */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#0d0d1a', minHeight: '400px', maxHeight: 'calc(100vh - 200px)' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #4A90E2', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
                Loading session recording...
              </div>
            </div>
          )}
          {error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: '0.9rem' }}>
              <div style={{ textAlign: 'center' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            </div>
          )}
          <div
            ref={playerContainerRef}
            style={{
              width: '100%', height: '100%',
              display: loading || error ? 'none' : 'block',
              overflow: 'hidden',
            }}
          />
          {/* Export overlay */}
          {isExporting && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
            }}>
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <div style={{ width: '200px', height: '6px', background: 'rgba(255,255,255,0.15)', borderRadius: '3px', margin: '0 auto 12px', overflow: 'hidden' }}>
                  <div style={{ width: `${exportProgress}%`, height: '100%', background: '#4A90E2', borderRadius: '3px', transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Generating MP4... {exportProgress}%</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>This may take a few minutes for long sessions</div>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        {!loading && !error && events.length > 0 && (
          <div style={controlsStyle}>
            {/* Play/Pause */}
            <button
              onClick={isPlaying ? handlePause : handlePlay}
              style={{
                width: '36px', height: '36px', border: 'none', borderRadius: '50%',
                background: '#4A90E2', color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#3a7bd5'}
              onMouseLeave={e => e.currentTarget.style.background = '#4A90E2'}
            >
              {isPlaying ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><polygon points="5 3 19 12 5 21"/></svg>
              )}
            </button>

            {/* Time */}
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', minWidth: '45px', fontFamily: 'monospace', flexShrink: 0 }}>
              {formatTime(currentTime)}
            </span>

            {/* Seek Bar */}
            <input
              type="range"
              min={0}
              max={100}
              step={0.1}
              value={progress}
              onChange={handleSeek}
              style={{
                flex: 1, height: '4px', appearance: 'none', background: `linear-gradient(to right, #4A90E2 ${progress}%, rgba(255,255,255,0.15) ${progress}%)`,
                borderRadius: '2px', cursor: 'pointer', outline: 'none',
              }}
            />

            {/* Duration */}
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', minWidth: '45px', fontFamily: 'monospace', flexShrink: 0, textAlign: 'right' }}>
              {formatTime(totalTime)}
            </span>

            {/* Speed Controls */}
            <div style={{ display: 'flex', gap: '4px', marginLeft: '8px', flexShrink: 0 }}>
              {[0.5, 1, 2, 4, 8].map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  style={{
                    padding: '3px 8px', border: speed === s ? '1px solid #4A90E2' : '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px', background: speed === s ? 'rgba(74,144,226,0.2)' : 'transparent',
                    color: speed === s ? '#93c5fd' : '#64748b', cursor: 'pointer',
                    fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.2s',
                  }}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Spin keyframe animation */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .replayer-wrapper { transform-origin: top left; }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; width: 14px; height: 14px;
          background: #4A90E2; border-radius: 50%; cursor: pointer; border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        }
      `}</style>
    </div>
  );
};

export default SessionPlayerModal;
